// Extracted from original/src/App.tsx:15037-15377 (`case "company"`: "إعدادات الورقة الرسمية" — the identity card),
// ADR-08 §2.1 "copy, then cut logic". [C35] Kept: every label, input and class string of the card body (company name,
// the two phones, address with its placeholder, manager / accountant names, digital commission rate, the show / hide
// profits toggle with its note, the logo block with upload / remove and its hint, the rose save button "requires the
// super code"). Cut: `tempCompanySettings` / `localDataService.saveCompanySettings` / `handleSecurityCheck("master")` →
// props in, callbacks out (the container saves through `PUT /settings` with the caller's own PIN, D-123; the old
// "super code" was one office-wide code, D-018); `FileReader` → `onLogoFile(file)` (the container reads it);
// `officeLocation` (the old storefront mirror of the address, D09 platform) — not stored here.
// Changed by decision: the profits toggle now covers every customer-facing document, voucher and share text (D-148,
// F-224); the logo limit is 300 KB, PNG / JPEG / WebP (O-4; old 2 MB any image); the digital commission is a decimal
// string (ADR-02 §2.9). Added (new element, owner sign-off V-97): "صيغة طباعة الإيصالات" — the A4 / 80 mm default of
// each single-operation voucher (D-149), in the same toggle style as the profits row.
import { Save, Upload } from 'lucide-react';
import { useT } from '../i18n-context.js';
import { SettingsCardFrame } from './Settings.js';

export type VoucherFormatKey = 'customer_entry' | 'transfer_voucher' | 'installment_receipt' | 'sales_invoice';

export interface CompanyCardValues {
  readonly name: string;
  readonly phone1: string;
  readonly phone2: string;
  readonly address: string;
  readonly managerName: string;
  readonly accountantName: string;
  readonly digitalCommissionRate: string;
  readonly showProfitInPrints: boolean;
  readonly voucherFormats: Readonly<Record<VoucherFormatKey, 'a4' | '80mm'>>;
}

export interface CompanyCardProps {
  readonly values: CompanyCardValues;
  readonly logoUrl: string | null;
  readonly dirty: boolean;
  readonly busy: boolean;
  readonly onChange: (patch: Partial<CompanyCardValues>) => void;
  readonly onSave: () => void;
  readonly onLogoFile: (file: File) => void;
  readonly onLogoRemove: () => void;
  readonly customIcon?: string;
}

const input = 'w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-lg px-3 py-2 text-xs dark:text-[#f0f6fc]';
const label = 'block text-[10px] font-bold text-gray-400 dark:text-[#8b949e] mb-1 uppercase tracking-widest';

export function CompanyCard(p: CompanyCardProps) {
  const t = useT();
  const v = p.values;
  const toggle = (on: boolean, active: string) =>
    `flex-1 py-1.5 px-3 rounded-md text-[10px] font-black whitespace-nowrap transition-all cursor-pointer ${on ? `${active} text-white shadow-xs` : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-[#f0f6fc]'}`;
  return (
    <SettingsCardFrame cardKey="company" {...(p.customIcon ? { customIcon: p.customIcon } : {})}>
      <div className="space-y-4" data-testid="company-card">
        <div>
          <label className={label}>{t('company.name')}</label>
          <input type="text" value={v.name} onChange={(e) => p.onChange({ name: e.target.value })} className={input} data-testid="company-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>{t('company.phone1')}</label>
            <input type="text" value={v.phone1} onChange={(e) => p.onChange({ phone1: e.target.value })} className={input} />
          </div>
          <div>
            <label className={label}>{t('company.phone2')}</label>
            <input type="text" value={v.phone2} onChange={(e) => p.onChange({ phone2: e.target.value })} className={input} />
          </div>
        </div>
        <div>
          <label className={label}>{t('company.address')}</label>
          <input type="text" value={v.address} onChange={(e) => p.onChange({ address: e.target.value })} className={input} placeholder={t('company.address_placeholder')} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>{t('company.manager')}</label>
            <input type="text" value={v.managerName} onChange={(e) => p.onChange({ managerName: e.target.value })} className={input} />
          </div>
          <div>
            <label className={label}>{t('company.accountant')}</label>
            <input type="text" value={v.accountantName} onChange={(e) => p.onChange({ accountantName: e.target.value })} className={input} />
          </div>
        </div>
        <div>
          <label className={label}>{t('company.digital_commission')}</label>
          <input type="text" inputMode="decimal" dir="ltr" value={v.digitalCommissionRate} onChange={(e) => p.onChange({ digitalCommissionRate: e.target.value })} className={`${input} font-mono`} placeholder="0.1" />
        </div>
        <div className="space-y-2">
          <label className="block text-[10px] font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-widest">{t('company.show_profits')}</label>
          <div className="flex bg-gray-100 dark:bg-[#05070a] p-1 rounded-lg border border-gray-200 dark:border-[#30363d] w-full">
            <button type="button" onClick={() => p.onChange({ showProfitInPrints: true })} className={toggle(v.showProfitInPrints, 'bg-emerald-600')} data-testid="company-profits-show">
              {t('company.show_profits_on')}
            </button>
            <button type="button" onClick={() => p.onChange({ showProfitInPrints: false })} className={toggle(!v.showProfitInPrints, 'bg-rose-600')} data-testid="company-profits-hide">
              {t('company.show_profits_off')}
            </button>
          </div>
          <p className="text-[9px] text-gray-400 dark:text-[#8b949e] italic font-medium">{t('company.show_profits_note')}</p>
        </div>
        {/* [C35] D-149 (V-97): the paper of each single-operation voucher */}
        <div className="space-y-2" data-testid="company-voucher-formats">
          <label className="block text-[10px] font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-widest">{t('company.voucher_formats')}</label>
          {(['customer_entry', 'transfer_voucher', 'installment_receipt', 'sales_invoice'] as const).map((k) => (
            <div key={k} className="flex items-center gap-2">
              <span className="flex-1 text-[10px] font-bold text-gray-600 dark:text-[#c9d1d9]">{t(`company.voucher.${k}`)}</span>
              <div className="flex bg-gray-100 dark:bg-[#05070a] p-1 rounded-lg border border-gray-200 dark:border-[#30363d] w-52 shrink-0">
                {(['a4', '80mm'] as const).map((f) => (
                  <button key={f} type="button" onClick={() => p.onChange({ voucherFormats: { ...v.voucherFormats, [k]: f } })} className={toggle(v.voucherFormats[k] === f, 'bg-blue-600')} data-testid={`company-format-${k}-${f}`}>
                    {t(`print.format.${f}`)}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <p className="text-[9px] text-gray-400 dark:text-[#8b949e] italic font-medium">{t('company.voucher_formats_note')}</p>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('company.logo')}</label>
          <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d]">
            <div className="rounded-xl border border-gray-200 dark:border-[#30363d] bg-white flex items-center justify-center overflow-hidden" style={{ width: 60, height: 60 }}>
              {p.logoUrl ? <img src={p.logoUrl} alt="" className="w-full h-full object-contain" data-testid="company-logo-preview" /> : <span className="text-[9px] font-black text-gray-300">LOGO</span>}
            </div>
            <div className="flex-1 space-y-2">
              <label className="flex items-center justify-center gap-2 w-full py-2 bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-lg text-[10px] font-black uppercase tracking-widest cursor-pointer hover:bg-gray-100 dark:hover:bg-[#161b22] transition-all dark:text-[#f0f6fc]">
                <Upload size={14} />
                {t('company.logo_upload')}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) p.onLogoFile(file);
                  }}
                  className="hidden"
                  data-testid="company-logo-input"
                />
              </label>
              {p.logoUrl && (
                <button type="button" onClick={p.onLogoRemove} className="w-full py-1.5 text-[10px] font-bold text-rose-500 hover:text-rose-600 transition-colors uppercase tracking-widest cursor-pointer">
                  {t('company.logo_remove')}
                </button>
              )}
            </div>
          </div>
          <p className="text-[9px] text-gray-400 dark:text-[#8b949e] mt-2 italic font-medium">{t('company.logo_hint')}</p>
        </div>
        <button
          type="button"
          onClick={p.onSave}
          disabled={!p.dirty || p.busy}
          data-testid="company-save"
          className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest flex items-center justify-center gap-2"
        >
          <Save size={16} />
          {t('company.save')}
        </button>
      </div>
    </SettingsCardFrame>
  );
}
